import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebRTC } from '../context/WebRTCContext';
import { useFeature } from './useFeatures';

const RETRY_MS = 1200;
const MAX_ATTEMPTS = 5;

/**
 * Shared consent-status source for ConsentPanel and ConsentBanner.
 *
 * Fetches the desktop's consent state via the consent feature, retrying a few
 * times to ride out the cold-start race (view mounts before the DataChannel is
 * open), then stays current via 'status_update' pushes and consent.* Sacred
 * Timeline events.
 *
 * @returns {{ status: Object|null, error: string|null, refresh: Function,
 *            feature: Object|null, hasPeer: boolean }}
 */
export function useConsentStatus() {
    const { connectedPeers } = useWebRTC();
    const feature = useFeature('consent');
    const sacredFeature = useFeature('sacred-timeline');
    const peerCount = connectedPeers?.length ?? 0;

    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const attemptsRef = useRef(0);
    const timerRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const fetchStatus = useCallback(async () => {
        if (!feature) return;
        try {
            const res = await feature.getStatus();
            if (!mountedRef.current) return;
            if (res?.success && res.state) {
                setStatus(res.state);
                setError(null);
                attemptsRef.current = 0;
                return;
            }
            throw new Error(res?.error || 'Consent status unavailable');
        } catch (err) {
            if (!mountedRef.current) return;
            if (attemptsRef.current < MAX_ATTEMPTS) {
                attemptsRef.current += 1;
                timerRef.current = setTimeout(fetchStatus, RETRY_MS);
            } else {
                setError(err.message || 'Consent status unavailable');
            }
        }
    }, [feature]);

    useEffect(() => {
        if (!feature || peerCount === 0) return undefined;
        attemptsRef.current = 0;
        fetchStatus();
        const unsub = feature.on('statusUpdate', (state) => {
            setStatus(state);
            setError(null);
        });
        return unsub;
    }, [feature, peerCount, fetchStatus]);

    // Lifecycle events (granted/revoked/expired/self-release) also ride the
    // Sacred Timeline — refresh on any of them so the panel never lags a push.
    useEffect(() => {
        if (!sacredFeature) return undefined;
        return sacredFeature.on('sacred_event', (eventData) => {
            const event = eventData?.payload || eventData;
            if (event?.type?.startsWith('consent.')) fetchStatus();
        });
    }, [sacredFeature, fetchStatus]);

    return { status, error, refresh: fetchStatus, feature, hasPeer: peerCount > 0 };
}
